import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Shield,
  AlertCircle,
  FileText,
  Receipt,
  BarChart3,
  FolderOpen,
  Settings,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Bell
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const Sidebar = ({ collapsed, onToggle }) => {
  const { user } = useAuth();

  const menuItems = [
    {
      path: '/dashboard',
      icon: LayoutDashboard,
      label: 'Dashboard',
      roles: ['admin', 'agente', 'vendedor', 'tecnico', 'cliente'],
      color: 'from-primary-500 to-primary-600'
    },
    {
      path: '/garantias',
      icon: Shield,
      label: 'Garantías',
      roles: ['admin', 'agente', 'vendedor', 'tecnico'],
      color: 'from-success-500 to-success-600'
    },
    {
      path: '/cliente/garantia',
      icon: Shield,
      label: 'Mi Garantía',
      roles: ['cliente'],
      color: 'from-success-500 to-success-600'
    },
    {
      path: '/averias',
      icon: AlertCircle,
      label: 'Averías',
      roles: ['admin', 'agente', 'vendedor', 'tecnico'],
      color: 'from-danger-500 to-danger-600'
    },
    {
      path: '/cliente/averias',
      icon: AlertCircle,
      label: 'Mis Averías',
      roles: ['cliente'],
      color: 'from-danger-500 to-danger-600'
    },
    {
      path: '/contratos',
      icon: FileText,
      label: 'Contratos',
      roles: ['admin', 'agente'],
      color: 'from-info-500 to-info-600'
    },
    {
      path: '/facturacion',
      icon: Receipt,
      label: 'Facturación',
      roles: ['admin', 'agente'],
      color: 'from-warning-500 to-warning-600'
    },
    {
      path: '/estadisticas',
      icon: BarChart3,
      label: 'Estadísticas',
      roles: ['admin', 'agente', 'vendedor'],
      color: 'from-secondary-500 to-secondary-600'
    },
    {
      path: '/documentos',
      icon: FolderOpen,
      label: 'Documentos',
      roles: ['admin', 'agente', 'vendedor', 'tecnico'],
      color: 'from-accent-500 to-accent-600'
    },
    {
      path: '/cliente/documentos',
      icon: FolderOpen,
      label: 'Mis Documentos',
      roles: ['cliente'],
      color: 'from-accent-500 to-accent-600'
    },
    {
      path: '/cliente/notificaciones',
      icon: Bell,
      label: 'Notificaciones',
      roles: ['cliente'],
      color: 'from-info-500 to-info-600'
    },
    {
      path: '/admin',
      icon: Settings,
      label: 'Administración',
      roles: ['admin'],
      color: 'from-gray-600 to-gray-700'
    },
    {
      path: '/cliente/configuracion',
      icon: Settings,
      label: 'Configuración',
      roles: ['cliente'],
      color: 'from-gray-600 to-gray-700'
    }
  ];

  const visibleItems = menuItems.filter(item => item.roles.includes(user?.role));

  return (
    <aside className={`glass-effect border-r-2 border-white/30 transition-all duration-300 ${collapsed ? 'w-20' : 'w-64'} flex flex-col shadow-2xl relative z-20`}>
      <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 via-secondary-500/5 to-accent-500/5"></div>

      {!collapsed && (
        <div className="px-6 py-6 border-b border-white/20 relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center shadow-lg animate-float">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-black bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                EUREKA
              </h1>
              <p className="text-xs text-gray-600 font-semibold">Gestión Garantías</p>
            </div>
          </div>
        </div>
      )}

      <nav className="flex-1 px-3 py-6 space-y-2 overflow-y-auto relative z-10">
        {visibleItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center px-4 py-3 rounded-xl transition-all duration-300 group relative overflow-hidden ${
                  isActive
                    ? 'glass-effect shadow-lg scale-105'
                    : 'hover:glass-effect hover:scale-105'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div className={`absolute inset-0 bg-gradient-to-r ${item.color} opacity-0 ${isActive ? 'opacity-10' : 'group-hover:opacity-5'} transition-opacity duration-300`}></div>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isActive ? `bg-gradient-to-br ${item.color} shadow-lg` : 'bg-gray-100 group-hover:bg-gradient-to-br group-hover:' + item.color} transition-all duration-300 relative z-10`}>
                    <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-gray-600 group-hover:text-white'} transition-colors duration-300`} />
                  </div>
                  {!collapsed && (
                    <span className={`ml-3 font-bold text-sm ${isActive ? 'text-gray-900' : 'text-gray-700 group-hover:text-gray-900'} transition-colors duration-300 relative z-10`}>
                      {item.label}
                    </span>
                  )}
                  {isActive && !collapsed && (
                    <div className="ml-auto">
                      <div className="w-2 h-2 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full animate-pulse"></div>
                    </div>
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="p-3 border-t border-white/20 relative z-10">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center px-4 py-3 glass-effect hover:bg-gradient-to-r hover:from-primary-500 hover:to-secondary-500 hover:text-white rounded-xl transition-all duration-300 group shadow-md hover:shadow-lg transform hover:scale-105"
        >
          {collapsed ? (
            <ChevronRight className="w-5 h-5 text-gray-700 group-hover:text-white transition-colors" />
          ) : (
            <>
              <ChevronLeft className="w-5 h-5 text-gray-700 group-hover:text-white transition-colors" />
              <span className="ml-2 text-sm font-bold text-gray-700 group-hover:text-white transition-colors">Contraer</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
